import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { HelpImageInput, HelpInboxReportCreate } from "@ttc-spectree/toolbox-contract/help";
import { helpInboxApi, type HelpInboxStatus } from "@/lib/api/help-inbox";
import { useAuthStore } from "@/stores/auth-store";

/**
 * TanStack Query hooks for the SpecTree Toolbox Help inbox (epic
 * PI-SPEC-SW-E302, feature PI-SPEC-SW-1559, ER-1655 Resolved Decision 5).
 * Reads are global-Admin only on the server; callers pass `enabled` so a
 * person who is not an Admin never asks (the API would answer 403). Every
 * mutation refreshes the lists and the open count.
 */
export const helpInboxKeys = {
  /**
   * Scoped to the signed-in account, so one Admin's cached reports are never
   * served to the next person who signs in without a reload.
   */
  get all() {
    return ["help-inbox", useAuthStore.getState().user?.id ?? "anonymous"] as const;
  },
  lists: () => [...helpInboxKeys.all, "list"] as const,
  list: (status: HelpInboxStatus) => [...helpInboxKeys.lists(), status] as const,
  count: () => [...helpInboxKeys.all, "count"] as const,
  details: () => [...helpInboxKeys.all, "detail"] as const,
  detail: (id: string) => [...helpInboxKeys.details(), id] as const,
  screenshot: (id: string, index: number) => [...helpInboxKeys.all, "screenshot", id, index] as const,
};

/** The inbox, newest first (`open` by default). */
export function useHelpInboxReports(status: HelpInboxStatus = "open", enabled = true) {
  return useQuery({
    queryKey: helpInboxKeys.list(status),
    queryFn: () => helpInboxApi.list(status),
    enabled,
  });
}

/** How many reports are open: the rail badge. Refetched when the window gets focus. */
export function useHelpInboxCount(enabled: boolean) {
  return useQuery({
    queryKey: helpInboxKeys.count(),
    queryFn: () => helpInboxApi.count(),
    select: (count) => count.open,
    enabled,
    refetchOnWindowFocus: true,
    staleTime: 30 * 1000,
  });
}

/** One whole report. */
export function useHelpInboxReport(id: string | null) {
  return useQuery({
    queryKey: helpInboxKeys.detail(id ?? ""),
    queryFn: () => helpInboxApi.get(id ?? ""),
    enabled: id !== null && id.length > 0,
  });
}

/**
 * One stored screenshot as an object URL for an `<img>` (the route needs the
 * Bearer token, so a plain `src` cannot reach it). The URL is revoked when
 * the blob changes or the component goes away.
 */
export function useHelpInboxScreenshotUrl(id: string, index: number): { url: string | null; failed: boolean } {
  const query = useQuery({
    queryKey: helpInboxKeys.screenshot(id, index),
    queryFn: () => helpInboxApi.screenshot(id, index),
    staleTime: Infinity,
    gcTime: 60 * 1000,
  });
  const blob = query.data;
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!blob || typeof URL.createObjectURL !== "function") {
      setUrl(null);
      return undefined;
    }
    const next = URL.createObjectURL(blob);
    setUrl(next);
    return () => {
      URL.revokeObjectURL(next);
    };
  }, [blob]);
  return { url, failed: query.isError };
}

/** Send one confirmed report. One call is one POST; nothing is retried on its own. */
export function useCreateHelpInboxReport() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ report, images }: { report: HelpInboxReportCreate; images: readonly HelpImageInput[] }) =>
      helpInboxApi.create(report, images),
    retry: false,
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: helpInboxKeys.lists() }),
        queryClient.invalidateQueries({ queryKey: helpInboxKeys.count() }),
      ]);
    },
  });
}

/** Mark a report handled (or open again). */
export function useSetHelpInboxHandled() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, handled }: { id: string; handled: boolean }) => helpInboxApi.setHandled(id, handled),
    onSuccess: async (_report, { id }) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: helpInboxKeys.lists() }),
        queryClient.invalidateQueries({ queryKey: helpInboxKeys.count() }),
        queryClient.invalidateQueries({ queryKey: helpInboxKeys.detail(id) }),
      ]);
    },
  });
}
